import { sha256 } from 'hash.js';
import { print } from 'graphql/language/printer';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import {
  createHttpLink,
  gql,
  ApolloClient,
  ApolloLink,
  Observable
} from '@apollo/client';
import { InMemoryCache } from '@apollo/client/cache';
import { RestLink } from 'apollo-link-rest';
import { relayStylePagination } from '@apollo/client/utilities';
import { onError } from '@apollo/client/link/error';
import { visit } from 'graphql/language/visitor';
import config from '../config';
import Bugsnag from './bugsnag';
import { hostname } from './helpers';
import { getXPlatform } from './helpers/getSSRPlatform';
import { ItemConnectionSort } from '../graphql/types';
import { mergeWithDedupe } from './helpers/mergeWithDedupe';
import { getServerHeaders } from './helpers/signRequest';

const isBrowser = typeof window !== 'undefined';
let apolloClient = null;
let currentClientLang = null;

/**
 * Returns true only for client-only operations where all top-level fields
 * are annotated with @client. Mixed operations (remote + @client fields)
 * are treated as non-client and go through the network.
 */
const isClientOnlyOperation = query => {
  let hasField = false;
  let allTopLevelClient = true;

  visit(query, {
    OperationDefinition(node) {
      if (!node.selectionSet || !node.selectionSet.selections) {
        return;
      }

      node.selectionSet.selections.forEach(selection => {
        if (selection.kind !== 'Field') {
          return;
        }

        hasField = true;

        const hasClient = selection.directives?.some(
          directive => directive.name?.value === 'client'
        );

        if (!hasClient) {
          allTopLevelClient = false;
        }
      });

      return false;
    }
  });

  return hasField && allTopLevelClient;
};

const DEFAULT_LOCAL_DATA = {
  rooms: [
    {
      id: '1',
      slug: '1-otaqli',
      name: '1 комната',
      __typename: 'Room'
    },
    {
      id: '2',
      slug: '2-otaqli',
      name: '2 комнаты',
      __typename: 'Room'
    },
    {
      id: '3',
      slug: '3-otaqli',
      name: '3 комнаты',
      __typename: 'Room'
    },
    {
      id: '4',
      slug: '4-otaqli',
      name: '4 комнаты',
      __typename: 'Room'
    },
    {
      id: '5+',
      slug: '5-otaqli',
      name: '5+',
      __typename: 'Room'
    }
  ],
  type: [
    {
      id: 'false',
      name: 'Alqi-satqi',
      __typename: 'Type'
    },
    {
      id: 'true',
      name: 'Kiraye',
      __typename: 'Type'
    }
  ],
  sort: [
    {
      id: '1',
      params: 'bumped_at+desc',
      name: ItemConnectionSort.BumpedAtDesc,
      __typename: 'Sort'
    },
    {
      id: '2',
      params: 'area',
      name: ItemConnectionSort.AreaAsc,
      __typename: 'Sort'
    },
    {
      id: '3',
      params: 'area+desc',
      name: ItemConnectionSort.AreaDesc,
      __typename: 'Sort'
    },
    {
      id: '4',
      params: 'price+asc',
      name: ItemConnectionSort.PriceAsc,
      __typename: 'Sort'
    },
    {
      id: '5',
      params: 'price+desc',
      name: ItemConnectionSort.PriceDesc,
      __typename: 'Sort'
    },
    {
      id: '6',
      params: 'price_per_are+asc',
      name: ItemConnectionSort.PricePerAreAsc,
      __typename: 'Sort'
    },
    {
      id: '7',
      params: 'price_per_are+desc',
      name: ItemConnectionSort.PricePerAreDesc,
      __typename: 'Sort'
    }
  ]
};

const DEFAULT_LOCAL_QUERY = gql`
  query defaultQuery {
    rooms @client {
      id
      slug
      name
    }
    type @client {
      id
      name
    }
    sort @client {
      id
      params
      name
    }
  }
`;

const createBaseUrl = lang =>
  `${config.protocol}://${hostname(lang, config.host)}`;

const createPersistedQueriesLink = () => {
  const basePersistedQueryLink = createPersistedQueryLink({
    useGETForHashedQueries: true,
    generateHash: query => sha256().update(print(query)).digest('hex')
  });

  return new ApolloLink((operation, forward) => {
    if (isClientOnlyOperation(operation.query)) {
      return forward(operation);
    }

    return basePersistedQueryLink.request(operation, forward);
  });
};

const createHttpLinks = lang => {
  const baseUrl = createBaseUrl(lang);

  const restLink = new RestLink({
    uri: `${baseUrl}/`
  });

  const baseHttpLink = createHttpLink({
    uri: `${baseUrl}/graphql`,
    credentials: 'include'
  });

  const httpLink = new ApolloLink((operation, forward) => {
    if (isClientOnlyOperation(operation.query)) {
      return Observable.of({ data: {} });
    }

    return baseHttpLink.request(operation, forward);
  });

  return { restLink, httpLink };
};

const EXPECTED_GQL_ERROR_CODES = new Set([
  'FORBIDDEN',
  'UNAUTHENTICATED',
  'NOT_FOUND',
  'LOCKED',
  'FAILURE'
]);

const isSystemGqlError = ({ extensions }) =>
  !extensions?.code || !EXPECTED_GQL_ERROR_CODES.has(extensions.code);

const isServerNetworkError = networkError => networkError?.statusCode >= 500;

const createErrorLink = () =>
  onError(({ graphQLErrors, networkError, operation }) => {
    if (graphQLErrors && graphQLErrors.length > 0) {
      if (!isBrowser) {
        console.error('[GraphQL]', operation?.operationName, graphQLErrors);
        return;
      }

      const systemErrors = graphQLErrors.filter(isSystemGqlError);

      if (systemErrors.length > 0) {
        Bugsnag.notify(new Error('GraphQlError'), event => {
          event.addMetadata('graphql', {
            operationName: operation?.operationName || 'unknown',
            errors: systemErrors.map(
              ({ message, locations, path, extensions }) => ({
                message,
                locations,
                path,
                code: extensions?.code
              })
            )
          });
        });
      }
    }

    if (networkError) {
      if (!isBrowser) {
        console.error(
          '[GraphQL NetworkError]',
          operation?.operationName,
          networkError
        );
        return;
      }

      if (isServerNetworkError(networkError)) {
        Bugsnag.notify(new Error('GraphQlNetworkError'), event => {
          event.addMetadata('graphql', {
            operationName: operation?.operationName || 'unknown',
            statusCode: networkError.statusCode,
            message: networkError.message
          });
        });
      }
    }
  });

const createCache = initialState =>
  new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          items: {
            keyArgs: ['filter', 'sort'],
            merge: mergeWithDedupe
          },
          itemsConnection: relayStylePagination(['filter', 'sort'])
        }
      },
      PaymentMethod: {
        keyFields: ['name']
      }
    }
  }).restore(initialState || {});

const primeCacheWithDefaults = cache => {
  if (isBrowser) {
    return;
  }

  cache.writeQuery({
    query: DEFAULT_LOCAL_QUERY,
    data: DEFAULT_LOCAL_DATA
  });
};

const createHeadersLink = ({
  platform,
  countryCode,
  ipAddr,
  userAgent,
  signature,
  timestamp,
  cookie
}) =>
  new ApolloLink((operation, forward) => {
    const xPlatform = getXPlatform(platform);

    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        ...getServerHeaders({
          userAgent,
          ipAddr,
          signature,
          timestamp,
          xPlatform,
          countryCode,
          cookie,
          setCookieCondition: !isBrowser
        })
      }
    }));

    return forward(operation);
  });

function createApolloClient(
  initialState,
  {
    lang = 'az',
    platform,
    countryCode,
    ipAddr,
    userAgent,
    signature,
    timestamp,
    cookie
  }
) {
  const cache = createCache(initialState);
  primeCacheWithDefaults(cache);

  const persistedQueryLink = createPersistedQueriesLink();
  const { restLink, httpLink } = createHttpLinks(lang);
  const headersMiddleware = createHeadersLink({
    platform,
    countryCode,
    ipAddr,
    userAgent,
    signature,
    timestamp,
    cookie
  });
  const errorLink = createErrorLink();

  const link = ApolloLink.from([
    errorLink,
    headersMiddleware,
    restLink,
    persistedQueryLink,
    httpLink
  ]);

  return new ApolloClient({
    connectToDevTools: isBrowser,
    ssrMode: !isBrowser,
    link,
    cache,
    resolvers: {}
  });
}

export default function initApollo(initialState, options = {}) {
  if (!isBrowser) {
    return createApolloClient(initialState, options);
  }

  // On client-side, recreate client if language changed
  // This ensures correct API endpoint is used after language switch
  const newLang = options?.lang;
  if (!apolloClient || (newLang && newLang !== currentClientLang)) {
    apolloClient = createApolloClient(initialState, options);
    currentClientLang = newLang;
  }

  return apolloClient;
}
